import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAccessibility } from "../context/AccessibilityContext";

const CVD_OPTIONS = [
  { value: "none", label: "Standard" },
  { value: "protanopia", label: "Red-Green (P)" },
  { value: "deuteranopia", label: "Red-Green (D)" },
  { value: "tritanopia", label: "Blue-Yellow" },
];

const TEXT_SCALES = ["base", "lg", "xl"];
const TEXT_SCALE_LABELS = { base: "A", lg: "A+", xl: "A++" };

function Toggle({ checked, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative h-6 w-11 flex-shrink-0 border transition-colors ${
        checked ? "border-forest-900 bg-forest-900" : "border-line-strong bg-transparent"
      }`}
      aria-label={label}
    >
      <span
        className={`absolute top-0.5 h-4 w-4 transition-transform ${
          checked ? "translate-x-[22px] bg-white" : "translate-x-0.5 bg-ink-faint"
        }`}
      />
    </button>
  );
}

export default function AccessibilityToolbar() {
  const { prefs, set, reset, speaking, paused, speak, togglePause, stopSpeaking, voiceInfo } = useAccessibility();
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Stop any in-progress narration when the route changes, so it never
  // keeps reading a page the visitor has already navigated away from.
  useEffect(() => {
    stopSpeaking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const textScaleIndex = TEXT_SCALES.indexOf(prefs.textScale);

  return (
    <div className="fixed bottom-5 right-5 z-[60]" data-a11y-exclude>
      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Accessibility preferences"
          className="fixed inset-x-4 bottom-24 max-h-[70vh] overflow-y-auto overscroll-contain border border-line-strong bg-surface p-5 shadow-lg sm:absolute sm:inset-x-auto sm:bottom-16 sm:right-0 sm:w-[21rem] sm:max-w-[calc(100vw-2.5rem)] sm:max-h-[calc(100vh-7rem)] sm:p-6"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="eyebrow">Accessibility</p>
              <h2 className="mt-1 font-serif text-lg text-heading">Make this page yours</h2>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close accessibility panel"
              className="text-xl leading-none text-ink-faint transition-colors hover:text-heading"
            >
              &times;
            </button>
          </div>
          <p className="mt-2 text-[0.82rem] leading-relaxed text-ink-soft">
            I built this because access shouldn&rsquo;t be an afterthought. Adjust anything below and it&rsquo;ll
            remember for next time.
          </p>

          <div className="mt-5 space-y-5 border-t border-line pt-5">
            {/* Theme */}
            <div>
              <p className="text-[0.75rem] uppercase tracking-widest2 text-ink-faint">Theme</p>
              <div className="mt-2 flex border border-line-strong">
                {["light", "dark"].map((t) => (
                  <button
                    key={t}
                    onClick={() => set({ theme: t })}
                    className={`flex-1 py-2 text-[0.75rem] uppercase tracking-widest2 transition-colors ${
                      prefs.theme === t ? "bg-forest-900 text-white" : "text-ink-soft hover:text-heading"
                    }`}
                    aria-pressed={prefs.theme === t}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Color vision */}
            <div>
              <p className="text-[0.75rem] uppercase tracking-widest2 text-ink-faint">Color Vision</p>
              <div className="mt-2 grid grid-cols-2 gap-1.5">
                {CVD_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    onClick={() => set({ cvd: o.value })}
                    aria-pressed={prefs.cvd === o.value}
                    className={`border px-2 py-1.5 text-[0.75rem] uppercase tracking-wide transition-colors ${
                      prefs.cvd === o.value
                        ? "border-forest-900 bg-forest-900 text-white"
                        : "border-line-strong text-ink-soft hover:text-heading"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Text size */}
            <div className="flex items-center justify-between">
              <p className="text-[0.75rem] uppercase tracking-widest2 text-ink-faint">Text Size</p>
              <div className="flex border border-line-strong">
                <button
                  onClick={() => set({ textScale: TEXT_SCALES[Math.max(0, textScaleIndex - 1)] })}
                  disabled={textScaleIndex === 0}
                  className="px-3 py-1.5 text-sm text-ink-soft transition-colors hover:text-heading disabled:opacity-30"
                  aria-label="Decrease text size"
                >
                  A&minus;
                </button>
                <span className="flex items-center border-x border-line-strong px-3 text-[0.75rem] text-heading">
                  {TEXT_SCALE_LABELS[prefs.textScale]}
                </span>
                <button
                  onClick={() => set({ textScale: TEXT_SCALES[Math.min(2, textScaleIndex + 1)] })}
                  disabled={textScaleIndex === 2}
                  className="px-3 py-1.5 text-sm text-ink-soft transition-colors hover:text-heading disabled:opacity-30"
                  aria-label="Increase text size"
                >
                  A+
                </button>
              </div>
            </div>

            {/* Reading space */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[0.82rem] text-heading">Reading space</p>
                <p className="text-[0.75rem] text-ink-faint">Wider line &amp; letter spacing</p>
              </div>
              <Toggle checked={prefs.readingSpace} onChange={(v) => set({ readingSpace: v })} label="Toggle reading space" />
            </div>

            {/* High contrast */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[0.82rem] text-heading">High contrast</p>
                <p className="text-[0.75rem] text-ink-faint">Removes faint, low-contrast text</p>
              </div>
              <Toggle checked={prefs.highContrast} onChange={(v) => set({ highContrast: v })} label="Toggle high contrast" />
            </div>

            {/* Read aloud */}
            <div className="border-t border-line pt-5">
              <p className="text-[0.82rem] text-heading">Read this page aloud</p>
              <p className="mt-1 text-[0.75rem] leading-relaxed text-ink-faint">
                Your browser&rsquo;s built-in voice, the same idea behind Extablify, built into my own site.
              </p>
              {voiceInfo.voice && (
                <p className="mt-1 text-[0.75rem] leading-relaxed text-ink-faint">
                  {voiceInfo.isGhanaian ? (
                    <>Using <strong className="font-medium text-link">{voiceInfo.voice.name}</strong>.</>
                  ) : (
                    <>
                      Using {voiceInfo.voice.name} ({voiceInfo.voice.lang}), no Ghanaian or local-language
                      voice is available in most browsers yet. Closing that gap is exactly what
                      Extablify&rsquo;s roadmap is building toward.
                    </>
                  )}
                </p>
              )}
              <div className="mt-3 flex gap-2">
                {!speaking ? (
                  <button
                    onClick={speak}
                    className="flex-1 border border-forest-900 bg-forest-900 py-2 text-[0.75rem] uppercase tracking-widest2 text-white transition-colors hover:bg-forest-700"
                  >
                    &#9654; Play
                  </button>
                ) : (
                  <>
                    <button
                      onClick={togglePause}
                      className="flex-1 border border-line-strong py-2 text-[0.75rem] uppercase tracking-widest2 text-ink-soft transition-colors hover:text-heading"
                    >
                      {paused ? "Resume" : "Pause"}
                    </button>
                    <button
                      onClick={stopSpeaking}
                      className="flex-1 border border-line-strong py-2 text-[0.75rem] uppercase tracking-widest2 text-ink-soft transition-colors hover:text-heading"
                    >
                      Stop
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          <button
            onClick={reset}
            className="mt-5 w-full border-t border-line pt-4 text-center text-[0.75rem] uppercase tracking-widest2 text-ink-faint transition-colors hover:text-heading"
          >
            Reset to defaults
          </button>
        </div>
      )}

      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? "Close accessibility preferences" : "Open accessibility preferences"}
        className="flex h-14 w-14 items-center justify-center rounded-full border border-forest-900 bg-forest-900 text-white shadow-lg transition-transform hover:scale-105"
      >
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.4" />
          <circle cx="12" cy="7.2" r="1.6" fill="currentColor" />
          <path
            d="M6.5 10.2c3.6 1.1 7.4 1.1 11 0M12 10.6v4.3m0 0-2.4 4.6M12 14.9l2.4 4.6"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  );
}
